<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h3>Live Note Tracker</h3>
        <v-card class="pa-4">
          <v-row>
            <v-col cols="12">
              <v-btn color="primary" @click="startListening" :disabled="listening">
                Start
              </v-btn>
              <v-btn color="red" @click="stopListening" :disabled="!listening">
                Stop
              </v-btn>
            </v-col>

            <v-col cols="12">
              <h4>Active Note:</h4>
              <span v-if="activeNote">{{ activeNote.note }} ({{ activeNote.midi }})</span>
              <span v-else>None</span>
            </v-col>

            <v-col cols="12">
              <h4>Recent Notes:</h4>
              <v-chip
                v-for="(note, index) in completedNotes.slice(-10)"
                :key="index"
                class="ma-1"
                color="green lighten-2"
              >
                {{ note.note }} ({{ note.duration.toFixed(2) }}s)
              </v-chip>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { YIN } from "pitchfinder";
import { fromMidi } from "@tonaljs/note";

export default {
  name: "MicNoteTracker",
  data() {
    return {
      audioCtx: null,
      processor: null,
      source: null,
      listening: false,
      detectPitch: null,

      // note tracking
      activeNote: null,
      completedNotes: [],
      noteBuffer: [],
      stableBuffer: [],

      // parameters
      MIN_MIDI: 60, // C4
      MAX_MIDI: 85, // C#5
      SMOOTHING_FRAMES: 5,
      STABILITY_FRAMES: 5,
      MIN_RMS: 0.002,
    };
  },
  methods: {
    hzToMidi(freq) {
      return 69 + 12 * Math.log2(freq / 440);
    },

    computeRMS(buffer) {
      const sumSquares = buffer.reduce((sum, val) => sum + val * val, 0);
      return Math.sqrt(sumSquares / buffer.length);
    },

    async startListening() {
      if (this.listening) return;

      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      this.detectPitch = YIN({ sampleRate: this.audioCtx.sampleRate });

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.source = this.audioCtx.createMediaStreamSource(stream);

      this.processor = this.audioCtx.createScriptProcessor(1024, 1, 1);
      this.source.connect(this.processor);
      this.processor.connect(this.audioCtx.destination);

      this.processor.onaudioprocess = (e) => {
        const input = e.inputBuffer.getChannelData(0);

        // ignore low-volume noise
        const rms = this.computeRMS(input);
        if (rms < this.MIN_RMS) {
          this.handleNoteOff();
          return;
        }

        const pitch = this.detectPitch(input);
        if (!pitch) {
          this.handleNoteOff();
          return;
        }

        let midi = Math.round(this.hzToMidi(pitch));

        if (midi < this.MIN_MIDI || midi > this.MAX_MIDI) {
          this.handleNoteOff();
          return;
        }

        // smoothing buffer
        this.noteBuffer.push(midi);
        if (this.noteBuffer.length > this.SMOOTHING_FRAMES) this.noteBuffer.shift();
        const avgMidi = Math.round(this.noteBuffer.reduce((a, b) => a + b, 0) / this.noteBuffer.length);

        // stability buffer
        this.stableBuffer.push(avgMidi);
        if (this.stableBuffer.length > this.STABILITY_FRAMES) this.stableBuffer.shift();

        const allSame = this.stableBuffer.every(val => val === this.stableBuffer[0]);
        if (allSame) {
          this.handleNoteOn(avgMidi);
        } else {
          this.handleNoteOff();
        }
      };

      this.listening = true;
    },

    stopListening() {
      if (!this.listening) return;

      this.processor.disconnect();
      this.source.disconnect();
      this.audioCtx.close();

      this.listening = false;
      this.activeNote = null;
      this.noteBuffer = [];
      this.stableBuffer = [];
    },

    handleNoteOn(midi) {
      const noteName = fromMidi(midi);
      const now = this.audioCtx.currentTime;

      // if same note is active, do nothing
      if (this.activeNote && this.activeNote.midi === midi) return;

      // if a different note was active, close it
      if (this.activeNote) {
        this.activeNote.endTime = now;
        this.activeNote.duration = this.activeNote.endTime - this.activeNote.startTime;
        this.completedNotes.push({ ...this.activeNote });
      }

      // start new active note
      this.activeNote = {
        note: noteName,
        midi,
        startTime: now,
        endTime: null,
        duration: null,
      };
    },

    handleNoteOff() {
      if (!this.activeNote) return;

      const now = this.audioCtx.currentTime;
      this.activeNote.endTime = now;
      this.activeNote.duration = this.activeNote.endTime - this.activeNote.startTime;
      this.completedNotes.push({ ...this.activeNote });
      this.activeNote = null;
    },
  },
};
</script>

<style scoped>
.v-chip {
  font-size: 1.2em;
  font-weight: bold;
}
</style>
